import { CatalogItem } from "../services/catalogApi";
import { PurchaseOrder } from "../services/purchaseOrderService";

export type ProductWithSales = {
  id: string;
  title: string;
  vendor?: string;
  sku?: string;
  price: number;
  cost: number;
  quantitySold: number;
  totalSales: number;
  totalCost: number;
  grossProfit: number;
  margin: number;
  totalInventory: number;
  daysRemaining: number;
};

const normalizeId = (value: any): string =>
  String(value?._id || value?.$oid || value || "");

const resolveInventory = (item: CatalogItem, storageId: string): number => {
  const stock = (item.storageStock || []).find(
    (s) => normalizeId(s.storageId) === normalizeId(storageId)
  );
  if (!stock) return 0;
  if (typeof stock.sellableQuantity === "number") return stock.sellableQuantity;
  if (typeof stock.totalQuantity === "number") return stock.totalQuantity;
  return 0;
};

export const buildProductSalesFromPurchaseOrders = (
  catalogItems: CatalogItem[],
  purchaseOrders: PurchaseOrder[],
  storageId: string,
  daysBetween: number
): ProductWithSales[] => {
  const salesByProductId: Record<string, { quantity: number; total: number }> =
    {};
  const skuToId = new Map<string, string>();

  catalogItems.forEach((item) => {
    const id = normalizeId(item.id || item._id || item.handle || item.Sku);
    if (!id) return;
    if (!salesByProductId[id]) {
      salesByProductId[id] = { quantity: 0, total: 0 };
    }
    if (item.Sku) skuToId.set(String(item.Sku), id);
  });

  purchaseOrders.forEach((po) => {
    (po.items || []).forEach((item) => {
      const productId = normalizeId(item.productId);
      const resolvedId = salesByProductId[productId]
        ? productId
        : skuToId.get(String(item.sku || "")) || "";
      if (!resolvedId || !salesByProductId[resolvedId]) return;
      const quantity = Number(item.quantity) || 0;
      const total =
        Number(item.totalPrice) || (Number(item.unitPrice) || 0) * quantity;
      salesByProductId[resolvedId].quantity += quantity;
      salesByProductId[resolvedId].total += total;
    });
  });

  return catalogItems
    .map((item) => {
      const id = normalizeId(item.id || item._id || item.handle || item.Sku);
      if (!id) return null;
      const sales = salesByProductId[id] || { quantity: 0, total: 0 };
      const quantitySold = sales.quantity;
      const totalSales = sales.total;
      const cost = Number(item.costPerItem) || 0;
      const totalCost = cost * quantitySold;
      const grossProfit = totalSales - totalCost;
      const margin = totalSales > 0 ? (grossProfit / totalSales) * 100 : 0;
      const price =
        quantitySold > 0 ? totalSales / quantitySold : Number(item.Price) || 0;
      const totalInventory = resolveInventory(item, storageId);
      const dailySales = daysBetween > 0 ? quantitySold / daysBetween : 0;
      const daysRemaining = dailySales > 0 ? totalInventory / dailySales : 999;

      return {
        id,
        title: item.title || item.handle || item.Sku || "Produit",
        vendor: item.vendor,
        sku: item.Sku,
        price,
        cost,
        quantitySold,
        totalSales,
        totalCost,
        grossProfit,
        margin: Number.isFinite(margin) ? Number(margin.toFixed(2)) : 0,
        totalInventory,
        daysRemaining: Number.isFinite(daysRemaining)
          ? Math.round(daysRemaining)
          : 999,
      };
    })
    .filter((item): item is ProductWithSales => Boolean(item));
};
